import React, { useState, useMemo } from 'react';
import DynamicDataTable from './DynamicDataTable';
import GraphSelectorModal from './GraphSelectorModal';
import ChartRenderer from './ChartRenderer';
import {
  Save,
  Download,
  BarChart3,
  Columns3,
  Star
} from 'lucide-react';
import ChartConfigPanel from './ChartConfigPanel';

type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'doughnut' | 'radar' | 'table';

const ChartDataView: React.FC<{ data: any[] }> = ({ data }) => {
  const [selectedChart, setSelectedChart] = useState<ChartType>('table');
  const [showGraphModal, setShowGraphModal] = useState(false);
  const allColumns = useMemo(() => Object.keys(data[0] || {}), [data]);

  const defaultX = allColumns.find(col => typeof data?.[0]?.[col] !== 'number') || allColumns[0];
  const defaultY = allColumns.filter(col => typeof data?.[0]?.[col] === 'number');

  const [visibleColumns, setVisibleColumns] = useState<string[]>(allColumns);
  const [labelsKey, setLabelsKey] = useState(defaultX);
  const [valueKeys, setValueKeys] = useState<string[]>(defaultY);
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  const handleExportCSV = () => {
    const headers = Object.keys(data[0] || {});
    const csv = [
      headers.join(','),
      ...data.map((row) => headers.map((key) => `"${row[key]}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'data.csv';
    a.click();
  };

  return (
    <div className="p-6 bg-white rounded shadow">
      {/* Top Layer: Actions */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <h1 className="text-xl font-semibold text-gray-800">Data Viewer</h1>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setShowGraphModal(true)}
            className="p-2 border rounded hover:bg-gray-100 text-gray-800"
            title="Choose Chart"
          >
            <BarChart3 className="w-5 h-5" />
          </button>

          {/* <button className="p-2 border rounded hover:bg-gray-100 text-gray-800" title="Save View">
            <Save className="w-5 h-5" />
          </button>
           */}
          <button className="p-2 border rounded hover:bg-gray-100 text-gray-800" title="Favorite">
            <Star className="w-5 h-5" />
          </button>

          <button
            onClick={handleExportCSV}
            className="p-2 border rounded hover:bg-gray-100 text-gray-800"
            title="Download CSV"
          >
            <Download className="w-5 h-5" />
          </button>

          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="p-2 border rounded text-gray-800 hover:bg-gray-100"
              title="Select Columns"
            >
              <Columns3 className="w-5 h-5" />
            </button>

            {showColumnSelector && (
              <div className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-md z-50 p-2 max-h-60 overflow-auto text-gray-800">
                {allColumns.map((col) => (
                  <label key={col} className="flex items-center gap-2 text-sm py-1">
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes(col)}
                      onChange={(e) => {
                        setVisibleColumns((prev) =>
                          e.target.checked ? [...prev, col] : prev.filter((c) => c !== col)
                        );
                      }}
                    />
                    {col}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart Selector Modal */}
      <GraphSelectorModal
        isOpen={showGraphModal}
        onClose={() => setShowGraphModal(false)}
        onSelect={(type) => setSelectedChart(type)}
      />

      {/* Optional Chart Axis Config */}
      <div className="min-w-[750px] overflow-x-auto">

        {selectedChart !== 'table' && (
          <ChartConfigPanel
            allKeys={visibleColumns}
            data={data}
            labelsKey={labelsKey}
            valueKeys={valueKeys}
            onLabelsKeyChange={setLabelsKey}
            onValueKeysChange={setValueKeys}
          />
        )}

        {/* Main Content View */}
        {selectedChart === 'table' ? (
          <DynamicDataTable data={data} visibleColumns={visibleColumns} />
        ) : (
          <div className="bg-white border rounded-lg p-4 shadow">
            <ChartRenderer
              type={selectedChart}
              data={data}
              labelsKey={labelsKey}
              valueKeys={valueKeys}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartDataView;
